import { PureComponent } from 'react';
import difference from 'lodash/difference';
import pick from 'lodash/pick';
import noop from 'lodash/noop';

// Get a list of built-in function names to exclude from static hoisting
const DEFAULT_FUNCTION_PROPERTIES = Object.getOwnPropertyNames(noop);

/**
 * Create a pure component from a stateless functional component
 *
 * React's stateless functional components cannot currently implement a `shouldComponentUpdate`
 * method, which means that they are re-rendered unnecessarily whenever the supplied props are
 * identical to the previous set of props.
 *
 * This function takes a stateless functional component as input, and returns a
 * performance-optimised version of the same component (which will skip any unnecessary re-rendering
 * when it is given identical prop values).
 *
 * Any static properties defined on the stateless functional component (e.g. `propTypes`) will be
 * copied across to the pure component.
 * @param {Function} component Stateless functional component
 * @return {PureComponent} Performance-optimised pure component
 * @example
 * import { pure } from '@dbk-legacy/muster/react';
 *
 * function HelloComponent(props) {
 *   return <h1>Hello, {props.user}!</h1>;
 * }
 *
 * HelloComponent.propTypes = {
 *   user: PropTypes.string,
 * };
 *
 * HelloComponent.defaultProps = {
 *   user: 'world',
 * };
 *
 * export default pure(HelloComponent);
 */
export default function pure(component) {
  // Only allow stateless functional components
  if ((typeof component !== 'function') || (typeof component.prototype.render === 'function')) {
    throw new Error('Invalid component passed to pure()' +
      ' – expected a stateless functional component'
    );
  }

  // Create a wrapper component with built-in dirty-checking
  class PureStatelessComponent extends PureComponent {
    render() {
      return component(this.props, this.context);
    }
  }

  // Copy static class properties to the wrapper component
  const ignoredKeys = DEFAULT_FUNCTION_PROPERTIES.concat('displayName');
  const staticKeys = difference(Object.getOwnPropertyNames(component), ignoredKeys);
  const staticProperties = pick(component, staticKeys);
  Object.assign(PureStatelessComponent, staticProperties, {
    displayName: `pure(${component.displayName || component.name || 'fn'})`,
  });

  // Return the wrapper component
  return PureStatelessComponent;
}
